// Platform (super-admin) API client — deliberately SEPARATE from the tenant api.ts:
// different token storage keys, no tenant header, its own refresh. Keeps the platform
// surface from ever sharing state with a tenant session.

const P_ACCESS = 'eduvora.platform.access';
const P_REFRESH = 'eduvora.platform.refresh';

export const platformTokens = {
  access: () => localStorage.getItem(P_ACCESS),
  refresh: () => localStorage.getItem(P_REFRESH),
  save(a: { accessToken: string; refreshToken: string }) {
    localStorage.setItem(P_ACCESS, a.accessToken);
    localStorage.setItem(P_REFRESH, a.refreshToken);
  },
  clear() {
    localStorage.removeItem(P_ACCESS);
    localStorage.removeItem(P_REFRESH);
  },
};

// Reuse the ONE base resolver (api.ts). Previously this file had its own
// `const BASE = ''`, which meant the platform console would have kept calling
// relative paths the moment VITE_API_BASE was set — a silent break.
import { API_BASE as BASE } from './api';

export class PlatformApiError extends Error {
  constructor(public status: number, message: string) { super(message); }
}

async function raw(method: string, path: string, body?: unknown, auth = true): Promise<Response> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (auth) {
    const t = platformTokens.access();
    if (t) headers['Authorization'] = `Bearer ${t}`;
  }
  return fetch(`${BASE}${path}`, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
}

async function handle<T>(res: Response): Promise<T> {
  if (res.status === 401) throw new PlatformApiError(401, 'Unauthorized');
  if (!res.ok) {
    let msg = `Request failed (${res.status})`;
    // ⚠ BOTH SHAPES. Older platform routes answer { error }, newer ones { message } like the rest
    // of the API. Reading only one meant half the server's carefully written sentences were
    // replaced on screen by "Request failed (400)".
    try {
      const j = await res.json();
      if (j?.error) msg = j.error;
      else if (j?.message) msg = j.message;
    } catch { /* ignore */ }
    throw new PlatformApiError(res.status, msg);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

// one retry on 401 via refresh
async function withRefresh<T>(fn: () => Promise<Response>): Promise<T> {
  let res = await fn();
  if (res.status === 401 && platformTokens.refresh()) {
    const r = await raw('POST', '/api/platform/auth/refresh', { refreshToken: platformTokens.refresh() }, false);
    if (r.ok) {
      const data = await r.json();
      platformTokens.save(data);
      res = await fn();
    }
  }
  return handle<T>(res);
}

/**
 * ═══ THE PIPELINE'S SECOND LOCK ═══
 *
 * ⚠ sessionStorage, NOT localStorage. It dies with the tab, so a laptop left open on a school desk
 * does not leave the pipeline unlocked tomorrow. In memory would be safer still, but it would mean
 * retyping the PIN on every page refresh, and a lock people find annoying is a lock people write on
 * a sticky note.
 *
 * ⚠ THE PIN IS SENT ON EVERY REQUEST, by design — see SalesEndpoints for why there is no token
 * exchange. A wrong PIN comes back 403, never 401, so it does not trip the token-refresh retry and
 * get counted twice against the lockout.
 */
const PIN_KEY = 'eduvora.sales.pin';
export const salesPin = {
  get: () => sessionStorage.getItem(PIN_KEY) ?? '',
  set: (v: string) => sessionStorage.setItem(PIN_KEY, v),
  clear: () => sessionStorage.removeItem(PIN_KEY),
};

async function rawSales(method: string, path: string, body?: unknown): Promise<Response> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const t = platformTokens.access();
  if (t) headers['Authorization'] = `Bearer ${t}`;
  const pin = salesPin.get();
  if (pin) headers['X-Sales-Pin'] = pin;
  return fetch(`${BASE}${path}`, {
    method, headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
}

export const salesApi = {
  get: <T>(path: string) => withRefresh<T>(() => rawSales('GET', path)),
  post: <T>(path: string, body?: unknown) => withRefresh<T>(() => rawSales('POST', path, body)),
  put: <T>(path: string, body?: unknown) => withRefresh<T>(() => rawSales('PUT', path, body)),
  // ⚠ DELETE carries the PIN header like everything else — it goes through rawSales, not raw.
  del: <T>(path: string) => withRefresh<T>(() => rawSales('DELETE', path)),
};

export const platformApi = {
  get: <T>(path: string) => withRefresh<T>(() => raw('GET', path)),
  post: <T>(path: string, body?: unknown) => withRefresh<T>(() => raw('POST', path, body)),
  put: <T>(path: string, body?: unknown) => withRefresh<T>(() => raw('PUT', path, body)),
  // auth (no bearer)
  requestOtp: (email: string) => raw('POST', '/api/platform/auth/otp/request', { email }, false).then(handle<{ sent: boolean }>),
  verifyOtp: (email: string, code: string) =>
    raw('POST', '/api/platform/auth/otp/verify', { email, code }, false).then(handle<PlatformAuthResult>),
};

// ---- types ----
export interface PlatformAdminView {
  id: string; email: string; displayName: string; isActive: boolean; createdAt: string; lastLoginAt: string | null;
  /** 'Owner' | 'Sales'. ⚠ Not a union type on purpose — a role the server adds later must not make
   *  the client fail to compile; it should render the unfamiliar name and gate to the safe side. */
  role: string;
}
export interface PlatformAuthResult {
  accessToken: string; refreshToken: string; accessExpiresAt: string; admin: PlatformAdminView;
}
export interface TenantSummary {
  id: string; slug: string; name: string; isActive: boolean; createdAt: string; userCount: number; studentCount: number;
}
export interface TenantView {
  id: string; slug: string; name: string; isActive: boolean; logoUrl: string | null;
  primaryColor: string; accentColor: string; supportEmail: string | null; createdAt: string;
}

// is this the platform surface? admin.<domain>, or ?platform for local dev
export function isPlatformHost(): boolean {
  const host = window.location.hostname;
  if (host.split('.')[0] === 'admin') return true;
  return new URLSearchParams(window.location.search).has('platform');
}

// ---- impersonation ----
export interface ImpersonationStarted {
  token: string; sessionId: string; tenantSlug: string; tenantName: string; expiresInMinutes: number;
}
export interface ImpersonationSessionView {
  id: string; operatorEmail: string; tenantId: string; reason: string;
  startedAt: string; expiresAt: string; endedAt: string | null; active: boolean;
}

// Start a read-only support session in a tenant. Returns an imp token (a tenant token marked
// imp=true, enforced read-only by the backend guard regardless of anything the UI does).
export function startImpersonation(tenantId: string, reason: string) {
  return platformApi.post<ImpersonationStarted>(`/api/platform/tenants/${tenantId}/impersonate`, { reason });
}

// Hand off to the tenant app in impersonation mode. We place the imp token where the tenant
// api.ts reads it, plus a DISTINCT marker so the tenant app knows to show the banner and can't
// confuse this with a normal login.
const IMP_MARKER = 'eduvora.imp';
export function launchImpersonation(s: ImpersonationStarted) {
  const expiresAt = Date.now() + s.expiresInMinutes * 60 * 1000;
  // the tenant app's token store key (kept in sync with lib/api.ts ACCESS_KEY)
  localStorage.setItem('eduvora.access', s.token);
  // impersonation is non-refreshable — deliberately DO NOT set a refresh token
  localStorage.removeItem('eduvora.refresh');
  localStorage.setItem(IMP_MARKER, JSON.stringify({
    sessionId: s.sessionId, tenantName: s.tenantName, tenantSlug: s.tenantSlug, expiresAt,
  }));

  // ⚠ This MUST leave the admin domain, AND carry the token with it.
  //
  // Two problems the old one-liner had:
  //  1. It was a RELATIVE navigation (`/?tenant=slug`). From admin.eduvoratech.com that lands on
  //     admin.eduvoratech.com/?tenant=slug — which still serves the PLATFORM CONSOLE, not the
  //     school's app. The operator bounced back to the same page and it looked like nothing happened.
  //  2. localStorage is PER-ORIGIN. A token written on admin.eduvoratech.com is invisible to
  //     demo.eduvoratech.com, so even after fixing (1) the school app would arrive with no session.
  //
  // So the handoff passes the session in the URL FRAGMENT (after #). A fragment is never sent to the
  // server and doesn't land in server logs or the Referer header, which makes it the least-bad place
  // to carry a short-lived, non-refreshable, read-only token. The tenant app consumes it on load and
  // immediately strips it from the address bar.
  const host = window.location.hostname;
  const parts = host.split('.');
  const isLocal = host === 'localhost' || host === '127.0.0.1' || /^\d+\.\d+\.\d+\.\d+$/.test(host);

  const payload = encodeURIComponent(JSON.stringify({
    t: s.token, sid: s.sessionId, name: s.tenantName, slug: s.tenantSlug, exp: expiresAt,
  }));

  if (!isLocal && parts.length >= 2) {
    // drop a leading reserved label (admin./app./www.) to get the registrable domain
    const base = ['www', 'app', 'api', 'admin'].includes(parts[0]) ? parts.slice(1) : parts;
    window.location.href =
      `${window.location.protocol}//${s.tenantSlug}.${base.join('.')}/#imp=${payload}`;
    return;
  }

  // local development: no subdomains, same origin — the ?tenant= form is correct here.
  window.location.href = `/?tenant=${encodeURIComponent(s.tenantSlug)}#imp=${payload}`;
}
